//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { APR, ERC20Wei } from '@shared/conversions'
import { StageName, yupApr, yupErc20WeiRequired, yupEthAddrRequired } from '@shared/Enums'
import { EthAddr, ReportingColumnVisibilityEnum, StringTypeEnum, toFieldMetaData } from '@shared/SchemaTypes'
import { ZeroAddress } from 'ethers'
import * as yup from 'yup'
import { InferType } from 'yup'
import { ChainConfigName } from './DeploymentConfigTypes'

export const LytReservesSchemaValues = {
  id: yup
    .string()
    .label('PoolEventId ')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'id',

        description: 'ID of event ',
        stringType: StringTypeEnum.ID,
      })
    ),
  stageName: yup
    .string<StageName>()
    .label('stageName')
    .required()
    .meta(toFieldMetaData({ propName: 'stageName', description: 'stageName' })),

  chainConfigurationName: yup
    .string<ChainConfigName>()
    .label('ChainConfigName')
    .required()
    .meta(toFieldMetaData({ propName: 'chainConfigurationName', description: 'chainConfigurationName' })),
  chainId: yup
    .number()
    .label('chainId')
    .required()
    .meta(toFieldMetaData({ propName: 'chainId', description: 'chainId' })),

  poolAddr: yupEthAddrRequired
    .label('Vault Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'poolAddr',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 2,
        columnWidth: 150,
        description: 'The smart-contract address of the loan vault. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),

  poolToken: yup
    .string()
    .label('Token')
    .default(' ')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'poolToken',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 3,
        columnWidth: 70,
        description: 'Token the Loan Vault',
      })
    ),

  poolName: yup
    .string()
    .label('Asset Type')
    .default(' ')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'poolName',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 4,
        description: 'Name of the Loan Vault',
      })
    ),

  apy: yupApr
    .required()
    .default('0')
    .label('APY')
    .meta(
      toFieldMetaData({
        propName: 'apy',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 5,
        columnWidth: 80,
        description: ' The indicative interest rate in basis points ',
        stringType: StringTypeEnum.APR,
      })
    ),

  tokenBalance: yupErc20WeiRequired
    .label('Holdings')
    .required()
    .default('0')
    .meta(
      toFieldMetaData({
        propName: 'tokenBalance',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 6,
        columnWidth: 80,
        description: 'tokenBalance ',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),

  assetBalance: yupErc20WeiRequired
    .label('Value')
    .required()
    .default('0')
    .meta(
      toFieldMetaData({
        propName: 'assetBalance',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 7,
        columnWidth: 80,
        description: 'assets ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  currentAssetValue: yupErc20WeiRequired
    .label('Current Asset Value')
    .required()
    .default('0')
    .meta(toFieldMetaData({ propName: 'currentAssetValue', description: 'currentAssetValue' })),

  percentReservesMin: yup
    .number()
    .required()
    .default(0)
    .label('Min % of Reserves')
    .meta(
      toFieldMetaData({
        propName: 'percentReservesMin',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 8,
        columnWidth: 80,
        description: 'Min percent of reserves',
        stringType: StringTypeEnum.APR,
      })
    ),
  percentReservesMax: yup
    .number()
    .required()
    .default(0)
    .label('Max % of Reserves')
    .meta(
      toFieldMetaData({
        propName: 'percentReservesMax',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 8,
        columnWidth: 80,
        description: 'Max percent of reserves',
        stringType: StringTypeEnum.APR,
      })
    ),
  depositPriority: yup
    .number()
    .required()
    .default(0)
    .label('Deposit Priority')
    .meta(
      toFieldMetaData({
        propName: 'depositPriority',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 9,
        columnWidth: 110,
        description: 'Percent of reserves',
        stringType: StringTypeEnum.APR,
      })
    ),
  withdrawalPriority: yup
    .number()
    .required()
    .default(0)
    .label('Withdraw Priority')
    .meta(
      toFieldMetaData({
        propName: 'withdrawalPriority',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 10,
        columnWidth: 80,
        description: 'Percent of reserves',
        stringType: StringTypeEnum.APR,
      })
    ),
  isLiquidityAsset: yup
    .boolean()
    .required()
    .default(false)

    .label('isLiquidityAsset')
    .meta(toFieldMetaData({ propName: 'isLiquidityAsset', description: 'isLiquidityAsset' })),
}

export const LytReservesSchema = yup.object(LytReservesSchemaValues)
// .label('PoolEventCTO')
// .meta(toFieldMetaData({ propName: 'PoolEventCTO', description: 'Vault Event' }))

export interface LytReservesCTO {
  id: string
  isLiquidityAsset: boolean
  stageName: StageName
  chainConfigurationName: ChainConfigName
  chainId: number
  poolAddr: EthAddr
  poolToken: string
  poolName: string

  apy: APR

  tokenBalance: ERC20Wei
  assetBalance: ERC20Wei
  currentAssetValue: ERC20Wei
  percentReservesMin: number
  percentReservesMax: number
  depositPriority: number
  withdrawalPriority: number
}

type LytReservesCTOInfer = InferType<typeof LytReservesSchema>

export const _testALytReserves: LytReservesCTO = {} as unknown as LytReservesCTOInfer
export const _testBLytReserves: LytReservesCTOInfer = {} as unknown as LytReservesCTO

export const LytReservesCTOAllocationSchema = yup.object({
  lytPoolAddr: yupEthAddrRequired,
  timestamp: yup.number(),
  reserves: yup.array().of(LytReservesSchema),
})

export interface LytReservesCTOAllocation {
  lytPoolAddr: EthAddr
  timestamp: number
  reserves: LytReservesCTO[]
}
